// Данные для тренировки в зале: что подтянуть заранее (шаблоны, справочник, план на сегодня,
// прошлые подходы), чтобы в зале без связи всё было на телефоне, и как читать их «из кэша,
// если сети нет».

import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { api } from '@/api/client'
import { todayIso } from '@/lib/periods'
import { useOnline } from '@/offline/queue'
import { planApi, planKeys, type Planned, type Prescription } from '@/pages/plan/api'
import { exerciseImageUrl, trainingApi, trainingKeys, type Exercise, type Template, type WorkoutSet } from '@/pages/workouts/api'

const FRESH_MS = 10 * 60 * 1000
const NETWORK_TIMEOUT_MS = 4000

export const plannedKey = (id: string) => ['plan', 'session', id] as const

function timeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then(
      (v) => {
        clearTimeout(timer)
        resolve(v)
      },
      (e: unknown) => {
        clearTimeout(timer)
        reject(e instanceof Error ? e : new Error(String(e)))
      },
    )
  })
}

/** Свежие данные, если сеть отвечает быстро; иначе — что есть в кэше (или undefined). */
export async function readThrough<T>(qc: QueryClient, queryKey: QueryKey, queryFn: () => Promise<T>): Promise<T | undefined> {
  const cached = qc.getQueryData<T>(queryKey)
  if (typeof navigator !== 'undefined' && !navigator.onLine) return cached
  try {
    return await timeout(qc.fetchQuery({ queryKey, queryFn, staleTime: FRESH_MS }), NETWORK_TIMEOUT_MS)
  } catch {
    return cached
  }
}

/** Прошлые подходы по упражнениям: id → подходы прошлой тренировки. */
export async function loadLast(qc: QueryClient, exerciseIds: string[]): Promise<(id: string) => WorkoutSet[]> {
  const unique = [...new Set(exerciseIds)]
  const entries = await Promise.all(
    unique.map(async (id) => [id, (await readThrough(qc, trainingKeys.last(id), () => trainingApi.last(id))) ?? []] as const),
  )
  const map = new Map(entries)
  return (id) => map.get(id) ?? []
}

export function cachedTemplates(qc: QueryClient): Template[] {
  return qc.getQueryData<Template[]>(trainingKeys.templates(false)) ?? []
}

/** Плановая тренировка по id: из закэшированного календаря или с сервера. */
export async function findPlanned(qc: QueryClient, id: string): Promise<Planned | undefined> {
  for (const [, data] of qc.getQueriesData<Planned[]>({ queryKey: ['plan', 'calendar'] })) {
    const hit = data?.find((p) => p.id === id)
    if (hit) return hit
  }
  return readThrough(qc, plannedKey(id), () => api.get<Planned>(`/plan/sessions/${id}`))
}

export function loadPrescription(qc: QueryClient, templateId: string, day: string): Promise<Prescription | undefined> {
  return readThrough(qc, planKeys.prescription(templateId, day), () => planApi.prescription(templateId, day))
}

/**
 * Пока есть связь — подтянуть в кэш (он сохраняется в IndexedDB) всё, что нужно в зале:
 * шаблоны, справочник, план на сегодня с целями и прошлые подходы упражнений.
 */
export async function prefetchWorkoutData(qc: QueryClient, day: string = todayIso()): Promise<void> {
  const opts = { staleTime: FRESH_MS }
  const [templates, calendar] = await Promise.all([
    qc.fetchQuery({ queryKey: trainingKeys.templates(false), queryFn: () => trainingApi.templates(false), ...opts }),
    qc.fetchQuery({ queryKey: planKeys.calendar(day, day), queryFn: () => planApi.calendar(day, day), ...opts }),
    qc.prefetchQuery({ queryKey: trainingKeys.exercises('', '', false), queryFn: () => trainingApi.exercises('', '', false), ...opts }),
    qc.prefetchQuery({ queryKey: trainingKeys.muscles, queryFn: trainingApi.muscles, ...opts }),
  ])
  const planned = calendar.filter((p) => p.kind === 'strength' && p.template_id && p.status !== 'done' && p.status !== 'skipped')
  for (const p of planned) qc.setQueryData(plannedKey(p.id), p)
  const prescriptions = await Promise.all(
    planned.map((p) =>
      qc.fetchQuery({
        queryKey: planKeys.prescription(p.template_id!, day),
        queryFn: () => planApi.prescription(p.template_id!, day),
        ...opts,
      }),
    ),
  )
  const ids = new Set<string>()
  for (const t of templates) for (const e of t.exercises) if (!e.exercise_archived) ids.add(e.exercise_id)
  for (const p of prescriptions) for (const e of p.exercises) ids.add(e.exercise_id)
  await Promise.all(
    [...ids].map((id) => qc.prefetchQuery({ queryKey: trainingKeys.last(id), queryFn: () => trainingApi.last(id), ...opts })),
  )
  warmExerciseImages(qc, ids)
}

/** Фото техники упражнений из шаблонов и плана — в кэш service worker, чтобы «Инфо» работало без сети. */
function warmExerciseImages(qc: QueryClient, ids: Set<string>): void {
  if (typeof fetch !== 'function' || typeof navigator === 'undefined' || !navigator.serviceWorker?.controller) return
  const catalog = qc.getQueryData<Exercise[]>(trainingKeys.exercises('', '', false)) ?? []
  for (const exercise of catalog) {
    if (!ids.has(exercise.id)) continue
    for (const name of exercise.images) fetch(exerciseImageUrl(name)).catch(() => undefined)
  }
}

/** Подтягивать данные для зала при старте приложения и при появлении связи. */
export function useWorkoutPrefetch(): void {
  const qc = useQueryClient()
  const online = useOnline()
  useEffect(() => {
    if (!online) return
    prefetchWorkoutData(qc).catch(() => undefined)
  }, [qc, online])
}

/** Текущее время, обновляется раз в `everyMs`. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(id)
  }, [everyMs])
  return now
}
